/**
 * ═══════════════════════════════════════════════════════════════════════
 * КОГДА ЭТО БЫЛО — ТРЕМЯ РАЗНЫМИ СЛОВАМИ, И РАЗЛИЧИЕ НЕ КОСМЕТИЧЕСКОЕ.
 *
 * В эталоне на экране поддержки время названо трижды и всякий раз
 * по-своему:
 *
 *   ▸ в строке списка — «сегодня, 11:20», «14 августа», «2 июля»;
 *   ▸ в шапке переписки — «открыто сегодня в 09:14»;
 *   ▸ у сообщения — просто «09:14».
 *
 * Мы во всех трёх местах печатали «21.08.2026, 12:00». Полная дата
 * у КАЖДОГО сообщения в переписке, которая шла один день, — это
 * двенадцать одинаковых дат подряд: они не сообщают ничего и мешают
 * читать то, ради чего переписку открыли.
 *
 * СЕГОДНЯ И ВЧЕРА СЧИТАЮТСЯ ПО МОСКВЕ, а не по часам браузера. Иначе
 * у продавца из Владивостока «сегодня» начинается на семь часов
 * раньше, чем у поддержки, и они говорят о разных днях.
 *
 * ЗДЕСЬ ТОЛЬКО СЧЁТ. Ни DOM, ни React.
 * ═══════════════════════════════════════════════════════════════════════
 */

const ПОЯС = 'Europe/Moscow';

const ВРЕМЯ = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: ПОЯС,
});

const ДЕНЬ_МЕСЯЦ = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  timeZone: ПОЯС,
});

const ПОЛНАЯ = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: ПОЯС,
});

/** Который это московский день: «2026-08-26». */
/* Формат — один на модуль: `день()` зовётся на каждое обращение
   в списке. Довод целиком — в `ui/chisla.ts`. */
const ISO_ДЕНЬ = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: ПОЯС,
});

function день(d: Date): string {
  return ISO_ДЕНЬ.format(d);
}

function сколькоДнейНазад(когда: Date, сейчас: Date): number {
  const a = день(когда);
  const b = день(сейчас);
  if (a === b) return 0;
  const вчера = день(new Date(сейчас.getTime() - 86_400_000));
  if (a === вчера) return 1;
  return 2;
}

/** Год у обеих дат один? Прошлогоднее без года читается как этогоднее. */
function тотЖеГод(когда: Date, сейчас: Date): boolean {
  return день(когда).slice(0, 4) === день(сейчас).slice(0, 4);
}

/** «сегодня, 11:20» · «вчера, 18:03» · «14 августа» · «02.07.2025». */
export function когдаВСписке(iso: string, сейчас = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';

  const назад = сколькоДнейНазад(d, сейчас);
  if (назад === 0) return `сегодня, ${ВРЕМЯ.format(d)}`;
  if (назад === 1) return `вчера, ${ВРЕМЯ.format(d)}`;
  return тотЖеГод(d, сейчас) ? ДЕНЬ_МЕСЯЦ.format(d) : ПОЛНАЯ.format(d);
}

/** «сегодня в 09:14» · «вчера в 18:03» · «14 августа» · «02.07.2025». */
export function когдаОткрыто(iso: string, сейчас = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';

  const назад = сколькоДнейНазад(d, сейчас);
  if (назад === 0) return `сегодня в ${ВРЕМЯ.format(d)}`;
  if (назад === 1) return `вчера в ${ВРЕМЯ.format(d)}`;
  return тотЖеГод(d, сейчас) ? ДЕНЬ_МЕСЯЦ.format(d) : ПОЛНАЯ.format(d);
}

/**
 * Время у сообщения: «09:14» сегодняшнему, «14 августа, 09:14» старому.
 *
 * ДЕНЬ У СТАРОГО ОБЯЗАТЕЛЕН. Переписка длится неделями, и голое
 * «09:14» посреди неё означает «какое-то утро»; вопрос «в какой день
 * вы это писали» в разборе задают постоянно.
 */
export function времяСообщения(iso: string, сейчас = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';

  const назад = сколькоДнейНазад(d, сейчас);
  if (назад === 0) return ВРЕМЯ.format(d);
  if (назад === 1) return `вчера, ${ВРЕМЯ.format(d)}`;
  const дата = тотЖеГод(d, сейчас) ? ДЕНЬ_МЕСЯЦ.format(d) : ПОЛНАЯ.format(d);
  return `${дата}, ${ВРЕМЯ.format(d)}`;
}

/**
 * Когда пришло уведомление — для колокольчика.
 *
 * «ТОЛЬКО ЧТО» ЕСТЬ, И ОНО НЕ УКРАШЕНИЕ. Уведомление о работах
 * приходит в тот же миг, когда его отправили, и «сегодня, 17:23»
 * рядом с ним заставляет сверять часы, чтобы понять — это про сейчас
 * или про утро. Дизайнер поставил в предпросмотре именно «только что»
 * (`nt-когда` в админке), и клиент обязан увидеть то же самое.
 *
 * Дальше — уже привычная лестница списка: «сегодня, 11:20»,
 * «вчера, 18:03», «14 августа», «02.07.2025».
 */
export function когдаПришло(iso: string, сейчас = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const минут = (сейчас.getTime() - d.getTime()) / 60_000;
  // Две минуты, а не одна: между отправкой и открытием колокольчика
  // проходит минута сама собой, и «1 минуту назад» читается как придирка.
  if (минут >= 0 && минут < 2) return 'только что';
  return когдаВСписке(iso, сейчас);
}
